import { useState, useRef } from "react";

import { Drawer as MuiDrawer } from "@mui/material";
import Avatar from "react-avatar";
import { useTranslation } from "react-i18next";
import ReactLoading from "react-loading";
import { useDispatch } from "react-redux";

import { logout } from "store/modules/auth/actions";
import { logoutThunk } from "store/modules/auth/actions/thunks";
import { hideLoading, showLoading } from "store/modules/loading/actions";
import { setScreenStatus } from "store/modules/screenControl/actions";
import { useAppSelector } from "store/rootReducer";

import { MyAccount, MyAccountRef } from "features/Modal/MyAccount";

import { ChangeLog } from "pages/app/ChangeLog";
import { getAllChangeLogs, groupVersions } from "pages/app/ChangeLog/constants";
import { ChangeLogData } from "pages/app/ChangeLog/interfaces";

import { AccessAppQr } from "components/AccessAppQr";
import { Drawer } from "components/Drawer";

import logo from "assets/Kenta_4.0_Judiciário.png";
import close from "assets/svg/close.svg";

import { LogOperation } from "utils/entities/logOperation";

import {
  ClientName,
  Container,
  ContainerClose,
  ContainerHeader,
  ContainerMenu,
  Label,
  LabelLink,
  Logo,
  PictureContainer,
  Separator,
  Subtitle,
  TitleHeader,
  styleImage,
} from "./style";

interface ModalType {
  isOpen: boolean;
  onClose: () => void;
  profile: string;
  setOpenPreferencesDrawer: (open: boolean) => any;
  customer?: string;
}

export const onClickSair = async ({ setIsLoading, dispatch }: any) => {
  try {
    dispatch(showLoading());
    setIsLoading(true);
    await LogOperation.postLogOperation("logout", 5, 5);
    await dispatch(logout());
    setIsLoading(false);
    dispatch(hideLoading());
  } catch {
    await dispatch(logout());
  } finally {
    setIsLoading(false);
    dispatch(hideLoading());
  }
};

const ProfileModal: React.FC<ModalType> = ({
  isOpen,
  onClose,
  profile,
  customer,
  setOpenPreferencesDrawer,
}) => {
  const { t } = useTranslation();
  const myAccountRef = useRef<MyAccountRef>(null);
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const [openAccessAppDrawer, setOpenAccessAppDrawer] =
    useState<boolean>(false);

  const [isLoading, setIsLoading] = useState(false);

  const dispatch = useDispatch<any>();

  const { user } = useAppSelector((state) => state.user);

  const [changeLogModal, setChangeLogModal] = useState<boolean>(false);
  const [changeLogData, setChangeLogData] = useState<ChangeLogData>();

  function clearAllCookies() {
    const cookies = document.cookie.split(";");

    for (const cookie of cookies) {
      const eqPos = cookie.indexOf("=");
      const name = eqPos > -1 ? cookie.substring(0, eqPos) : cookie;
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
    }
  }

  const onClickChangeLog = async () => {
    setChangeLogModal(true);
    const result = await getAllChangeLogs();
    const groupedVersionsAux = groupVersions(result);
    setChangeLogData(groupedVersionsAux);
  };

  const onApply = async () => {
    await myAccountRef.current?.handleSubmitForm();
  };

  return (
    <>
      <MuiDrawer
        data-testid="profile_modal_container"
        anchor="right"
        open={isOpen}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: 300,
            height: "100vh",
          },
        }}
      >
        <Container>
          <div>
            <ContainerClose>
              <img
                src={close}
                style={{ ...styleImage }}
                onClick={() => onClose()}
                alt=""
              />
            </ContainerClose>
            <ContainerHeader>
              {user?.picture ? (
                <PictureContainer
                  size={60}
                  src={`data:image/png;base64,${user.picture}`}
                  style={{ cursor: "default" }}
                />
              ) : (
                <Avatar
                  name={user?.name || "Avatar"}
                  size="60"
                  round={true}
                  style={{ cursor: "default" }}
                />
              )}
              <TitleHeader>{user?.name}</TitleHeader>
              <Subtitle>{profile}</Subtitle>
              <ClientName>{customer}</ClientName>
            </ContainerHeader>
            {isLoading ? (
              <ReactLoading
                type="spin"
                color="#F2A60D"
                width={24}
                height={24}
              />
            ) : (
              <ContainerMenu>
                <Separator />
                <Label
                  style={{ cursor: "pointer" }}
                  onClick={() => setOpenDrawer(true)}
                >
                  {t("profile.myAccount")}
                </Label>

                <Label
                  style={{ cursor: "pointer", marginTop: 10 }}
                  onClick={() => setOpenPreferencesDrawer(true)}
                >
                  {t("profile.deviceSettings")}
                </Label>
                <Separator />
                <Label
                  style={{ cursor: "pointer", marginBottom: 8 }}
                  onClick={() => setOpenAccessAppDrawer(true)}
                >
                  {t("profile.accessApp")}
                </Label>
                {/* <TitleMenu>Suporte</TitleMenu> */}
                <LabelLink
                  href="https://www.kenta.com.br/contato/"
                  target="blank"
                >
                  {t("profile.supportContact")}
                </LabelLink>
                <LabelLink href="https://kenta.ellevocloud.com/" target="blank">
                  {t("profile.openTicket")}
                </LabelLink>
                <Label>Versão 4.0.2 - 16/09/2026</Label>
                <div style={{ marginTop: "10px" }}>
                  <Label
                    style={{ cursor: "pointer" }}
                    //onClick={() => setChangeLogModal(true)}
                    onClick={() => onClickChangeLog()}
                  >
                    {t("profile.whatsNew")}
                  </Label>
                </div>
                <Separator />
                <Label
                  id="profile_modal_sair"
                  onClick={async () => {
                    dispatch(logoutThunk());
                    await onClickSair({
                      setIsLoading,
                      dispatch,
                    });
                    dispatch(setScreenStatus(false));
                    clearAllCookies();
                  }}
                  style={{
                    cursor: "pointer",
                  }}
                >
                  {t("profile.logout")}
                </Label>
              </ContainerMenu>
            )}
          </div>
          <Logo style={{ alignSelf: "center", width: "100px" }} src={logo} />
        </Container>
      </MuiDrawer>

      <Drawer
        open={openDrawer}
        onClose={() => setOpenDrawer(false)}
        title={t("profile.myAccount")}
        onApply={onApply}
      >
        <MyAccount ref={myAccountRef} setOpenDrawer={setOpenDrawer} />
      </Drawer>
      <Drawer
        open={openAccessAppDrawer}
        onClose={() => setOpenAccessAppDrawer(false)}
        title={t("profile.accessApp")}
        footer="none"
      >
        <AccessAppQr />
      </Drawer>
      <ChangeLog
        changeLogModal={changeLogModal}
        setChangeLogModal={setChangeLogModal}
        changeLogData={changeLogData}
      />
    </>
  );
};

export default ProfileModal;
